// Aba Escritório (0.45, grátis): a equipe num lugar só.
// Quem cria administra: adiciona amigos direto ou convida pelo link (WhatsApp, e-mail...), troca mensagens e documentos,
// marca reuniões (chegam em Recebidos de cada um para pôr no Planner) e faz reunião de vídeo, que o administrador pode gravar.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { abrirUrlComApp } from '../lib/abrirArquivo';
import { ActivityIndicator, Alert, Image, Linking, Platform, Pressable, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { ActionSheet, Btn, Chip, Field, Screen, Section, Sheet, type Action } from '../components/ui';
import { useConfirmarIdentidade } from '../components/ConfirmarIdentidade';
import { C, alpha, useCores } from '../components/theme';
import { PickerModal } from '../components/Pickers';
import { CampoRico } from '../components/CampoRico';
import { ScrollArrastavel } from '../components/Arrastar';
import type { RicoCampo } from '../store/types';
import { convidarPara } from '../components/Grupos';
import { semBloqueio } from '../auth/AuthContext';
import { Arquivados } from '../components/Selecao';
import { acoesDoMenu, useAcoes } from '../components/Acoes';
import { useOrganizar } from '../lib/organizar';
import { useBotaoNovo } from '../lib/botaoNovo';
import { setDoc, useDoc } from '../store/store';
import { addDays, brYmd, hm, horaTela, parseYmd, today, ymd } from '../lib/date';
import { noAparelho } from '../lib/fuso';
import { t } from '../lib/i18n';
import { lerBase64 } from '../lib/anexos';
import { idiomaResposta, iaPronta, perguntar } from '../lib/ia';
import { useAnexar } from '../components/MenuAnexo';
import { abrirChamada } from '../lib/chamada';
import { carregarAmigos, conversaDoGrupo, useChat } from '../lib/chat';
import {
  apagarGrupo, entrarGrupo, membrosDe, recarregarGrupos, removerMembro, sairGrupo, setAdmin, trocarCodigo, useMeusGrupos, meuId,
  type Membro, type MeuGrupo,
} from '../lib/grupos';
import { gerarPedidosDoMes } from '../modulos/portal';
import { ClientesEsc, useNumerosClientes, type Filtro } from '../components/escritorio/ClientesEsc';
import { RelatorioMes } from '../components/escritorio/RelatorioMes';
import { BoxCard, SwipeBox } from '../components/BoxCard';
import { CartaoApoio } from '../components/Recolhivel';
import { Ordenavel } from '../components/Arrastar';
import { archiveBox, ordenarCaixas } from '../store/anotacoes';
import { useNovoComNota as useNovoComNotaDe } from '../components/NotaRapida';
import { MembroSheet, Mural, SO_VE, TarefaSheet, TarefasEquipe, cargoDe, minhasAbertas, papelDe, recarregarTarefas, useInfoMembros, useTarefasEsc } from '../components/escritorio/Equipe96';
import {
  adicionarNaEquipe, apagarDoc, cancelarReuniao, comentar, comentariosDe, criarEscritorio, docVisto, docsDe, enviarDoc, iconeDoc, marcarReuniao, mudarDoc,
  organizarDoc, passarAdmin, proximaVez, respostasDe, responderReuniao, reunioesDe, salvarAta, tamanho, urlDoDoc, vistosDosDocs,
  type Comentario, type DocEsc, type RespReuniao, type Reuniao, type Visto,
} from '../lib/escritorio';

const bytesDe = (b64: string) => { const bin = atob(b64); const u = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i); return u; };
const falha = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
const pessoas = (n: number) => (n === 1 ? t('1 pessoa') : t('{n} pessoas', { n }));
const dataCurta = (iso: string) => { const d = new Date(iso); return `${brYmd(ymd(d))} ${hm(d)}`; };   // data local (0.77)

type Parte = 'clientes' | 'equipe' | 'reunioes' | 'docs';

/** Texto 27 (B1/B2): Escritório & Clientes. Junta as abas Escritório e Portal do Cliente: no topo o escritório escolhido,
 *  o painel com 4 quadros e as 4 partes (Clientes · Equipe · Reuniões · Documentos internos). Quem trabalha sozinho usa só
 *  os Clientes. Nada mudou de lugar: os escritórios (equipe, conversa, reuniões, documentos) e as pastas dos clientes
 *  (documentos e pedidos do antigo Portal) continuam onde estavam. */
export default function Escritorio({ item, onBack }: { item?: string; onBack: () => void }) {
  const { corDe } = useCores();
  const cor = corDe('escritorio');
  const router = useRouter();
  const doc = useDoc('escritorio');
  const gs = useMeusGrupos();
  const lista = useMemo(() => (gs.lista ?? []).filter(g => g.aba === 'escritorio'), [gs.lista]);
  const [aberto, setAberto] = useState<string | null>(item ?? doc.aberto ?? null);
  const [parte, setParte] = useState<Parte>(item ? 'equipe' : 'clientes');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [form, setForm] = useState<'novo' | 'codigo' | null>(null);
  const [trocar, setTrocar] = useState(false);
  const [nome, setNome] = useState('');
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [reunHoje, setReunHoje] = useState(0);
  const [conv, setConv] = useState<string | null>(null);
  const nums = useNumerosClientes();
  const g = lista.find(x => x.id === aberto) ?? (lista.length ? lista[0] : undefined);
  // texto 27 (B5): "Minhas tarefas" no painel = tarefas da equipe em que sou responsável e que não terminei
  const [euId, setEuId] = useState('');
  useEffect(() => { meuId().then(setEuId); }, []);
  const tarefas = useTarefasEsc(g?.id);
  const [soMinhas, setSoMinhas] = useState(false);
  const [relMes, setRelMes] = useState(false);
  const abrir = (id: string | null) => { setAberto(id); if (id) setDoc('escritorio', d => ({ ...d, aberto: id })); };

  useEffect(() => {
    setConv(null); setReunHoje(0);
    if (!g) return;
    conversaDoGrupo(g.id).then(setConv).catch(() => setConv(null));
    reunioesDe(g.id).then(rs => {
      const hoje = today();
      setReunHoje(rs.filter(r => (r.fuso ? noAparelho({ time: r.hora, tz: r.fuso }, r.data).dia : r.data) === hoje).length);
    }).catch(() => {});
  }, [g?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // texto 27 (B4.2): pedidos que se repetem todo mês: cria o do mês, uma vez
  useEffect(() => { try { gerarPedidosDoMes(); } catch { /* segue */ } }, []);
  // texto 27 (B1): uma vez, o aviso de que as duas abas viraram uma só, com os números (nada se perde)
  const aj = useDoc('ajustes');
  const portal = useDoc('portal')?.regs ?? [];
  useEffect(() => {
    if (!gs.lista || (aj as any).escClientesAviso) return;
    const cli = portal.filter(r => r.col === 'cliente').length, docs = portal.filter(r => r.col === 'documento').length, ped = portal.filter(r => r.col === 'pedido').length;
    setDoc('ajustes', d => ({ ...d, escClientesAviso: true }) as any);
    if (!lista.length && !cli) return;
    Alert.alert(t('Escritório & Clientes'), [t('As abas Escritório e Portal do Cliente agora são uma aba só.'),
      t('Escritórios: {n}', { n: lista.length }), t('Pastas de clientes do Portal: {c} (com {d} documentos e {p} pedidos)', { c: cli, d: docs, p: ped }),
      t('Nada foi apagado: a equipe, a conversa, as reuniões, os documentos e os clientes convidados continuam como estavam.')].join('\n\n'));
  }, [gs.lista]); // eslint-disable-line react-hooks/exhaustive-deps

  const criar = async () => {
    if (!nome.trim()) return;
    setOcupado(true);
    try { const r = await criarEscritorio(nome.trim()); setNome(''); setForm(null); abrir(r.id); setParte('equipe'); }
    catch (e) { falha(e); } finally { setOcupado(false); }
  };
  const entrar = async () => {
    if (!codigo.trim()) return;
    setOcupado(true);
    try { const x = await entrarGrupo(codigo.trim()); await recarregarGrupos(); setCodigo(''); setForm(null); if (x.aba === 'escritorio') abrir(x.id); else Alert.alert(t('Pronto'), t('Você entrou no grupo "{nome}".', { nome: x.nome })); }
    catch (e) { falha(e); } finally { setOcupado(false); }
  };
  const conversa = () => (conv ? router.push({ pathname: '/conversa', params: { id: conv } } as any) : Alert.alert(t('Aguarde'), t('Preparando a conversa da equipe...')));
  const reuniaoAgora = () => (g && conv ? abrirChamada(conv, 'video', g.nome) : Alert.alert(t('Aguarde'), t('Preparando a conversa da equipe...')));
  const ir = (p: Parte, f?: Filtro) => { setParte(p); if (f) setFiltro(f); };
  const quadro = (k: string, icone: string, rot: string, n: string, corN: string, fn: () => void, sub?: string) => (
    <Pressable key={k} onPress={fn} accessibilityRole="button" style={{ flexBasis: '47%', flexGrow: 1, backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 2 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{icone} {rot}</Text>
      <Text style={{ color: corN, fontSize: 22, fontWeight: '800' }}>{n}</Text>
      {!!sub && <Text style={{ color: C.mute, fontSize: 10 }}>{sub}</Text>}
    </Pressable>
  );
  const semEscritorio = (
    <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 16, gap: 10 }}>
      <Text style={{ fontSize: 30, textAlign: 'center' }}>👥</Text>
      <Text style={{ color: C.text, fontWeight: '700', textAlign: 'center' }}>{t('Esta parte é para quem trabalha com equipe')}</Text>
      <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Crie o escritório e chame a equipe (amigos do app ou convite pelo link): conversa, reuniões de vídeo e documentos internos. Trabalha sozinho? Use só os Clientes.')}</Text>
      <Btn label={'＋ ' + t('Criar escritório')} cor={cor} onPress={() => setForm('novo')} />
      <Btn label={t('Entrar com um código de convite')} cor={cor} outline onPress={() => setForm('codigo')} />
    </View>
  );
  return (
    <Screen title={t('Escritório & Clientes')} onBack={onBack}>
      {/* B2: o escritório escolhido; tocar troca ou cria outro */}
      {g ? (
        <Pressable onPress={() => setTrocar(true)} accessibilityRole="button" style={{ backgroundColor: alpha(cor, 0.14), borderRadius: 16, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ fontSize: 24 }}>🏢</Text>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }} numberOfLines={1}>{g.nome}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{pessoas(g.membros)}{g.admin ? ' · ' + t('você administra') : ''}</Text>
          </View>
          <Text style={{ color: C.sub }}>▾</Text>
        </Pressable>
      ) : gs.lista ? (
        <Pressable onPress={() => setTrocar(true)} style={{ backgroundColor: C.card, borderRadius: 16, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ fontSize: 22 }}>🏢</Text>
          <Text style={{ color: C.sub, flex: 1, fontSize: 13 }}>{t('Sem escritório (equipe). Toque para criar ou entrar com um código.')}</Text>
          <Text style={{ color: C.sub }}>▾</Text>
        </Pressable>
      ) : <ActivityIndicator color={cor} />}
      {!!gs.erro && <Text style={{ color: C.danger }}>{gs.erro}</Text>}
      {form === 'novo' && (
        <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 10 }}>
          <Field value={nome} onChangeText={setNome} placeholder={t('Nome do escritório (ex.: Ravasco Contabilidade)')} autoFocus maxLength={120} onSubmitEditing={criar} />
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Btn label={t('Cancelar')} cor={cor} outline onPress={() => setForm(null)} style={{ flex: 1 }} />
            <Btn label={ocupado ? '...' : t('Criar')} cor={cor} disabled={ocupado || !nome.trim()} onPress={criar} style={{ flex: 1 }} />
          </View>
        </View>
      )}
      {form === 'codigo' && (
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Field value={codigo} onChangeText={x => setCodigo(x.toUpperCase())} placeholder={t('Código do convite')} autoCapitalize="characters" autoFocus style={{ flex: 1 }} onSubmitEditing={entrar} />
          <Btn label={t('Entrar')} cor={cor} outline disabled={ocupado || !codigo.trim()} onPress={entrar} />
        </View>
      )}
      {!!g && (
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Pressable onPress={conversa} style={{ flex: 1, backgroundColor: C.card, borderRadius: 14, padding: 12, alignItems: 'center', gap: 2 }}>
            <Text style={{ fontSize: 20 }}>💬</Text><Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>{t('Conversa da equipe')}</Text>
          </Pressable>
          <Pressable onPress={reuniaoAgora} style={{ flex: 1, backgroundColor: alpha(cor, 0.2), borderRadius: 14, padding: 12, alignItems: 'center', gap: 2 }}>
            <Text style={{ fontSize: 20 }}>🎥</Text><Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>{t('Reunião agora')}</Text>
          </Pressable>
        </View>
      )}
      {/* B2: painel com 4 quadros tocáveis (levam à parte certa, já filtrada) */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {quadro('p', '🔴', t('Clientes com pendência'), String(nums.pendencia), nums.pendencia ? C.danger : C.text, () => ir('clientes', 'pendencia'))}
        {quadro('g', '🧾', t('Guias vencendo'), String(nums.guias), nums.guias ? C.warn : C.text, () => ir('clientes', 'vencendo'), t('nos próximos 7 dias'))}
        {quadro('r', '🎥', t('Reuniões hoje'), g ? String(reunHoje) : '—', C.text, () => ir('reunioes'))}
        {quadro('t', '✅', t('Minhas tarefas'), g && tarefas ? String(minhasAbertas(tarefas, euId).length) : '—', C.text, () => { setSoMinhas(true); ir('equipe'); })}
      </View>
      {/* B8: relatório do mês (do escritório ou de um cliente) */}
      <Pressable onPress={() => setRelMes(true)} accessibilityRole="button" style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 20 }}>📊</Text>
        <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{t('Relatório do mês')}</Text>
        <Text style={{ color: C.mute, fontSize: 18 }}>›</Text>
      </Pressable>
      <NotasRapidas cor={cor} />
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Chip on={parte === 'clientes'} label={'🗂️ ' + t('Clientes')} cor={cor} onPress={() => setParte('clientes')} />
        <Chip on={parte === 'equipe'} label={'👥 ' + t('Equipe')} cor={cor} onPress={() => setParte('equipe')} />
        <Chip on={parte === 'reunioes'} label={'📅 ' + t('Reuniões')} cor={cor} onPress={() => setParte('reunioes')} />
        <Chip on={parte === 'docs'} label={'📁 ' + t('Documentos internos')} cor={cor} onPress={() => setParte('docs')} />
      </View>
      {parte === 'clientes' && <><BotaoNovoCliente /><ClientesEsc cor={cor} filtro={filtro} setFiltro={setFiltro} /></>}
      {parte === 'equipe' && (g ? <Equipe key={g.id} g={g} cor={cor} onSaiu={() => abrir(null)} soMinhas={soMinhas} setSoMinhas={setSoMinhas} /> : semEscritorio)}
      {parte === 'reunioes' && (g ? <Reunioes key={g.id} g={g} cor={cor} onEntrar={reuniaoAgora} /> : semEscritorio)}
      {parte === 'docs' && (g ? (<>
        {/* B7: só a equipe vê */}
        <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>🔒 {t('Só a equipe vê. Clientes nunca veem esta parte.')}</Text>
        <Documentos key={g.id} g={g} cor={cor} />
      </>) : semEscritorio)}
      <ActionSheet title={t('Escritório')} actions={trocar ? [
        ...lista.map(x => ({ label: (x.id === g?.id ? '✓ ' : '🏢 ') + x.nome + ' · ' + pessoas(x.membros), onPress: () => abrir(x.id) })),
        { label: '＋ ' + t('Criar escritório'), onPress: () => setForm('novo') },
        { label: '🔑 ' + t('Entrar com um código de convite'), onPress: () => setForm('codigo') },
      ] : null} onClose={() => setTrocar(false)} />
      <RelatorioMes visible={relMes} onClose={() => setRelMes(false)} g={g} cor={cor} />
      <View style={{ height: 90 }} />
    </Screen>
  );
}

// ─── Texto 27 (B9): o + oferece "Item completo" (o que faz sentido na parte aberta) e "📝 Nota rápida" ───
// A nota rápida é uma caixa igual à da aba Anotações e fica em "Anotações recebidas", logo abaixo do painel.
const useNovoComNota = (item: { rotulo: string; fn: () => void } | null, extras: Action[] = [], rotulo?: string) => useNovoComNotaDe('escritorio', item, extras, rotulo);

/** O + na parte Clientes: cliente novo (cadastrado em Meus clientes, que é a lista desta aba). */
function BotaoNovoCliente() {
  const router = useRouter();
  return useNovoComNota({ rotulo: t('Novo cliente'), fn: () => router.push({ pathname: '/aba/[id]', params: { id: 'clientes' } } as any) });
}

/** Notas rápidas do Escritório (caixas da aba Anotações guardadas aqui). */
function NotasRapidas({ cor }: { cor: string }) {
  const caixas = useDoc('anotacoes').boxes.filter(b => b.aba === 'escritorio' && !b.archivedAt && !b.ref);
  if (!caixas.length) return null;
  return (
    <CartaoApoio titulo={'📝 ' + t('Anotações recebidas')} chave="esc:notas" inicial resumo={String(caixas.length)} cor={cor} gap={8}>
      <Ordenavel itens={caixas} chave={b => b.id} alca gap={8} onOrdem={ordenarCaixas}
        render={(b, _i, a) => <SwipeBox box={b}><BoxCard box={b} alca={a.alca} onArquivar={() => archiveBox(b.id)} /></SwipeBox>} />
    </CartaoApoio>
  );
}

// ─── Documentos internos ─────────────────────────────────────────────────────────────────────
// Texto 27 (B7): pastas (Contratos, RH, Modelos…), busca, fixar no topo (para todos), miniatura das fotos, VERSÕES (arquivo
// com o mesmo nome guarda o anterior como "versão 1, 2…"), "visto por" (quem da equipe abriu) e comentários.
const chaveNome = (d: DocEsc) => d.nome.trim().toLowerCase();
function Documentos({ g, cor }: { g: MeuGrupo; cor: string }) {
  const [docs, setDocs] = useState<DocEsc[] | null>(null);
  const [vistos, setVistos] = useState<(Visto & { doc_id: string })[]>([]);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [busca, setBusca] = useState('');
  const [pastaSel, setPastaSel] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [renomear, setRenomear] = useState<DocEsc | null>(null);
  const [moverPasta, setMoverPasta] = useState<DocEsc | null>(null);
  const [janela, setJanela] = useState<DocEsc | null>(null);
  const [eu, setEu] = useState('');
  const { info } = useInfoMembros(g.id);
  const org = useOrganizar('escritorio-doc');   // 📦 arquivar fica neste aparelho (não muda nada para a equipe)
  const { enviar: enviarAba } = useAcoes();
  const carregar = useCallback(() => {
    docsDe(g.id).then(d => { setDocs(d); setErro(''); vistosDosDocs(d.map(x => x.id)).then(setVistos); }, e => setErro(String(e?.message ?? e)));
  }, [g.id]);
  useEffect(() => { carregar(); meuId().then(setEu); }, [carregar]);
  const podeEditar = g.admin || papelDe(info, eu) !== 've';
  // gravação em andamento ou sendo preparada: confere de novo de tempos em tempos
  useEffect(() => {
    if (!docs?.some(d => d.status !== 'pronto')) return;
    const i = setInterval(carregar, 30000);
    return () => clearInterval(i);
  }, [docs, carregar]);

  // 0.72 (11g): o mesmo menu de anexos do app (Fotos · Tirar foto · Arquivos); semNuvem: o arquivo vai para o escritório
  const anexo = useAnexar({ cor, semNuvem: true });
  const enviar = async () => {
    if (!podeEditar) return Alert.alert(t('Só vê'), SO_VE());
    try {
      const r = await anexo.anexar({ tipo: 'tudo', titulo: t('Enviar documento') });
      const a = r?.anexo;
      if (!a) return;
      setEnviando(true);
      const b64 = await lerBase64(a);
      if (!b64) throw new Error(t('Não foi possível ler o arquivo.'));
      const antes = (docs ?? []).filter(d => d.tipo !== 'gravacao' && chaveNome(d) === a.nome.trim().toLowerCase());
      await enviarDoc(g.id, { bytes: bytesDe(b64), mime: a.mime || 'application/octet-stream', nome: a.nome });
      // mesma pasta da versão anterior
      if (antes.length && antes[0].pasta) {
        const novos = await docsDe(g.id);
        const n = novos.find(d => chaveNome(d) === a.nome.trim().toLowerCase() && !antes.some(x => x.id === d.id));
        if (n) await organizarDoc(n, { pasta: antes[0].pasta }).catch(() => {});
      }
      if (antes.length) Alert.alert(t('Nova versão'), t('Já havia um arquivo com este nome: o anterior fica guardado como versão {n}.', { n: antes.length }));
      carregar();
    } catch (e) { falha(e); } finally { setEnviando(false); }
  };
  const novoTela = useNovoComNota(podeEditar ? { rotulo: t('Enviar documento'), fn: enviar } : null);
  const abrirArq = async (d: DocEsc) => {
    try { const u = await urlDoDoc(d); docVisto(d).then(() => vistosDosDocs((docs ?? []).map(x => x.id)).then(setVistos)); await semBloqueio(() => abrirUrlComApp(u, d.mime, d.nome)); } catch (e) { falha(e); }
  };
  const apagarConf = (d: DocEsc) => Alert.alert(t('Apagar "{nome}"?', { nome: d.nome }), t('O arquivo sai do escritório para todos.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Apagar'), style: 'destructive', onPress: () => apagarDoc(d).then(carregar, falha) }]);
  const opcoes = (d: DocEsc) => {
    const a: Action[] = [{ label: '📂 ' + t('Abrir ou baixar'), onPress: () => abrirArq(d) },
      { label: '💬 ' + t('Visto por e comentários'), onPress: () => setJanela(d) },
      ...acoesDoMenu(enviarAba, {}),
      { label: '📦 ' + t('Arquivar (só neste aparelho)'), onPress: () => org.arquivar(d.id) }];
    if (g.admin) a.push({ label: d.fixado ? '📌 ' + t('Tirar do topo') : '📌 ' + t('Fixar no topo (para todos)'), onPress: () => organizarDoc(d, { fixado: !d.fixado }).then(carregar, falha) });
    if (g.admin || d.enviado_por === eu) a.push({ label: '🗂️ ' + t('Mover para a pasta…'), onPress: () => setMoverPasta(d) });
    if (g.admin || d.enviado_por === eu) a.push({ label: '✏️ ' + t('Renomear'), onPress: () => setRenomear(d) });
    if (g.admin) a.push(d.visivel
      ? { label: '🔒 ' + t('Esconder da equipe (só administradores)'), onPress: () => mudarDoc(d, { visivel: false }).then(carregar, falha) }
      : { label: '👥 ' + t('Liberar para a equipe'), onPress: () => mudarDoc(d, { visivel: true }).then(carregar, falha) });
    if (g.admin || d.enviado_por === eu) a.push({ label: '🗑 ' + t('Apagar'), destructive: true, onPress: () => apagarConf(d) });
    setMenu({ t: d.nome, a });
  };
  const sep = org.separar(docs ?? [], d => d.id);
  // versões: arquivos com o mesmo nome; o mais novo aparece, os anteriores ficam como "versão 1, 2…"
  const versoesDe = (d: DocEsc) => (docs ?? []).filter(x => x.tipo !== 'gravacao' && chaveNome(x) === chaveNome(d)).sort((a, b) => a.criado_em.localeCompare(b.criado_em));
  const q = busca.trim().toLowerCase();
  const ativos = sep.ativos.filter(d => d.tipo === 'gravacao' || versoesDe(d).slice(-1)[0]?.id === d.id);
  const filtrados = ativos.filter(d => (!q || d.nome.toLowerCase().includes(q) || (d.pasta ?? '').toLowerCase().includes(q)) && (pastaSel === null || (d.pasta ?? '') === pastaSel));
  const pastas = [...new Set((docs ?? []).map(d => d.pasta ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const gravacoes = filtrados.filter(d => d.tipo === 'gravacao');
  const arquivos = filtrados.filter(d => d.tipo !== 'gravacao');
  const fixados = arquivos.filter(d => d.fixado);
  const soltos = arquivos.filter(d => !d.fixado);
  const porPasta = [{ p: '', l: soltos.filter(d => !d.pasta) }, ...pastas.map(p => ({ p, l: soltos.filter(d => d.pasta === p) }))].filter(x => x.l.length);
  const vistosDe = (d: DocEsc) => vistos.filter(v => v.doc_id === d.id);
  const Linha = ({ d }: { d: DocEsc }) => {
    const nv = versoesDe(d).length, vs = vistosDe(d).length;
    return (
      <Pressable onPress={() => setJanela(d)} onLongPress={() => opcoes(d)} delayLongPress={1000} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Miniatura d={d} />
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={2}>{d.nome}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>
            {[d.tam ? tamanho(d.tam) : '', d.enviado_nome, dataCurta(d.criado_em)].filter(Boolean).join(' · ')}</Text>
          <View style={{ flexDirection: 'row', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
            {d.status === 'gravando' && <Text style={{ color: '#EF4444', fontSize: 12, fontWeight: '700' }}>⏺ {t('Gravando')}</Text>}
            {d.status === 'processando' && <Text style={{ color: C.sub, fontSize: 12 }}>⏳ {t('Preparando o arquivo')}</Text>}
            {!d.visivel && <Text style={{ color: C.sub, fontSize: 12 }}>🔒 {t('Só administradores')}</Text>}
            {nv > 1 && <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>🕘 {t('versão {n}', { n: nv })}</Text>}
            {vs > 0 && <Text style={{ color: C.sub, fontSize: 12 }}>👀 {t('visto por {n}', { n: vs })}</Text>}
          </View>
        </View>
        {!!d.fixado && <Text style={{ fontSize: 12 }}>📌</Text>}
        <Pressable onPress={() => opcoes(d)} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: C.sub, fontSize: 20, fontWeight: '800', paddingHorizontal: 4 }}>⋮</Text></Pressable>
      </Pressable>
    );
  };
  return (
    <View style={{ gap: 10 }}>
      {podeEditar && <Btn label={enviando ? t('Enviando...') : '📎 ' + t('Enviar documento')} cor={cor} disabled={enviando} onPress={enviar} />}
      <Text style={{ color: C.mute, fontSize: 12 }}>{t('Até 50 MB por arquivo. Enviar um arquivo com o mesmo nome guarda o anterior como versão.')}</Text>
      {(docs?.length ?? 0) > 4 && <Field value={busca} onChangeText={setBusca} placeholder={'🔎 ' + t('Procurar documento ou pasta')} />}
      {!!pastas.length && (
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          <Chip small cor={cor} on={pastaSel === null} label={t('Todas')} onPress={() => setPastaSel(null)} />
          {pastas.map(p => <Chip key={p} small cor={cor} on={pastaSel === p} label={'🗂️ ' + p} onPress={() => setPastaSel(pastaSel === p ? null : p)} />)}
        </View>
      )}
      {!docs && !erro && <ActivityIndicator color={cor} />}
      {!!erro && <Text style={{ color: C.danger }}>{erro}</Text>}
      {docs && !docs.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 10 }}>{t('Nenhum documento ainda.')}</Text>}
      {!!fixados.length && <Section t={'📌 ' + t('Fixados')} />}
      {fixados.map(d => <Linha key={d.id} d={d} />)}
      {porPasta.map(x => (
        <View key={x.p || '_'} style={{ gap: 10 }}>
          {(!!x.p || !!fixados.length || porPasta.length > 1) && <Section t={x.p ? '🗂️ ' + x.p : t('Sem pasta')} />}
          {x.l.map(d => <Linha key={d.id} d={d} />)}
        </View>
      ))}
      {!!gravacoes.length && <Section t={t('Gravações das reuniões')} />}
      {gravacoes.map(d => <Linha key={d.id} d={d} />)}
      <Arquivados itens={sep.arquivados} chave={d => d.id} cor={cor} render={d => <Text style={{ color: C.text }} numberOfLines={2}>{iconeDoc(d)} {d.nome}</Text>}
        onRestaurar={d => org.restaurar(d.id)} onExcluir={apagarConf} podeExcluir={d => g.admin || d.enviado_por === eu} />
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      <RenomearSheet doc={renomear} cor={cor} onClose={() => setRenomear(null)} onPronto={carregar} />
      {novoTela}
      <PastaSheet doc={moverPasta} pastas={pastas} cor={cor} onClose={() => setMoverPasta(null)} onPronto={carregar} />
      <DocJanela d={janela} versoes={janela ? versoesDe(janela) : []} vistos={janela ? vistosDe(janela) : []} cor={cor} podeEditar={podeEditar}
        onAbrir={abrirArq} onMais={d => { setJanela(null); setTimeout(() => opcoes(d), 300); }} onClose={() => setJanela(null)} />
      {anexo.tela}
    </View>
  );
}

/** Miniatura no cartão: a própria foto (quando é imagem); os outros, o ícone do tipo. */
const urlsMini = new Map<string, string>();
function Miniatura({ d }: { d: DocEsc }) {
  const [u, setU] = useState(urlsMini.get(d.id) ?? '');
  const foto = d.mime.startsWith('image/') && d.status === 'pronto';
  useEffect(() => {
    if (!foto || u) return;
    urlDoDoc(d).then(x => { urlsMini.set(d.id, x); setU(x); }, () => {});
  }, [d.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (foto && u) return <Image source={{ uri: u }} style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: C.card2 }} resizeMode="cover" />;
  return <Text style={{ fontSize: 24, width: 40, textAlign: 'center' }}>{iconeDoc(d)}</Text>;
}

/** A janela do documento: abrir, versões, quem da equipe viu e os comentários. */
function DocJanela({ d, versoes, vistos, cor, podeEditar, onAbrir, onMais, onClose }: { d: DocEsc | null; versoes: DocEsc[]; vistos: Visto[]; cor: string; podeEditar: boolean;
  onAbrir: (d: DocEsc) => void; onMais: (d: DocEsc) => void; onClose: () => void }) {
  const [coments, setComents] = useState<Comentario[] | null>(null);
  const [texto, setTexto] = useState('');
  const carregar = useCallback(() => { if (d) comentariosDe(d.id).then(setComents, () => setComents([])); }, [d]);
  useEffect(() => { setComents(null); setTexto(''); carregar(); }, [carregar]);
  const enviar = () => { if (!d || !texto.trim()) return; comentar(d.id, texto.trim()).then(() => { setTexto(''); carregar(); }, falha); };
  return (
    <Sheet visible={!!d} onClose={onClose} title={d?.nome ?? ''} right={d ? <Pressable onPress={() => onMais(d)} hitSlop={10}><Text style={{ color: C.sub, fontSize: 20, fontWeight: '800' }}>⋮</Text></Pressable> : undefined}>
      <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
        {!!d && <Btn label={'📂 ' + t('Abrir o documento')} cor={cor} onPress={() => onAbrir(d)} />}
        {!!d && <Text style={{ color: C.sub, fontSize: 12 }}>{[d.pasta ? '🗂️ ' + d.pasta : '', d.tam ? tamanho(d.tam) : '', t('enviado por {nome}', { nome: d.enviado_nome }), dataCurta(d.criado_em)].filter(Boolean).join(' · ')}</Text>}
        {versoes.length > 1 && (<>
          <Section t={t('Versões ({n})', { n: versoes.length })} />
          {versoes.slice().reverse().map((v, i) => (
            <Pressable key={v.id} onPress={() => onAbrir(v)} style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10 }}>
              <Text style={{ color: C.text }}>{i === 0 ? '⭐ ' + t('Versão {n} (atual)', { n: versoes.length - i }) : '🕘 ' + t('Versão {n}', { n: versoes.length - i })}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{v.enviado_nome} · {dataCurta(v.criado_em)}</Text>
            </Pressable>
          ))}
        </>)}
        <Section t={t('Visto por ({n})', { n: vistos.length })} />
        {!vistos.length && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Ninguém da equipe abriu ainda.')}</Text>}
        {vistos.map(v => <Text key={v.user_id} style={{ color: C.sub, fontSize: 13 }}>👀 {t('{nome} em {d}', { nome: v.nome || t('Sem nome'), d: dataCurta(v.em) })}</Text>)}
        <Section t={t('Comentários ({n})', { n: coments?.length ?? 0 })} />
        {!coments && <ActivityIndicator color={cor} />}
        {(coments ?? []).map(c => (
          <View key={c.id} style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, gap: 2 }}>
            <Text style={{ color: C.sub, fontSize: 11 }}>{c.nome} · {dataCurta(c.em)}</Text>
            <Text style={{ color: C.text }}>{c.texto}</Text>
          </View>
        ))}
        {podeEditar && (
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-end' }}>
            <Field value={texto} onChangeText={setTexto} placeholder={t('Escreva um comentário')} multiline maxLength={1000} style={{ flex: 1 }} />
            <Btn label={t('Enviar')} cor={cor} disabled={!texto.trim()} onPress={enviar} />
          </View>
        )}
      </ScrollArrastavel>
    </Sheet>
  );
}

function PastaSheet({ doc, pastas, cor, onClose, onPronto }: { doc: DocEsc | null; pastas: string[]; cor: string; onClose: () => void; onPronto: () => void }) {
  const [nome, setNome] = useState('');
  useEffect(() => { if (doc) setNome(doc.pasta ?? ''); }, [doc]);
  const mover = (p: string) => { if (!doc) return; organizarDoc(doc, { pasta: p }).then(() => { onClose(); onPronto(); }, falha); };
  const sugestoes = [...new Set([...pastas, t('Contratos'), t('Financeiro do escritório'), t('RH'), t('Modelos')])];
  return (
    <Sheet visible={!!doc} onClose={onClose} title={t('Mover para a pasta')}>
      <View style={{ padding: 16, gap: 12 }}>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {sugestoes.map(p => <Chip key={p} cor={cor} on={(doc?.pasta ?? '') === p} label={'🗂️ ' + p} onPress={() => mover(p)} />)}
          {!!doc?.pasta && <Chip cor={cor} on={false} label={t('Sem pasta')} onPress={() => mover('')} />}
        </View>
        <Field value={nome} onChangeText={setNome} placeholder={t('Ou escreva o nome de uma pasta nova')} maxLength={60} onSubmitEditing={() => nome.trim() && mover(nome.trim())} />
        <Btn label={t('Mover')} cor={cor} disabled={!nome.trim()} onPress={() => mover(nome.trim())} />
      </View>
    </Sheet>
  );
}

function RenomearSheet({ doc, cor, onClose, onPronto }: { doc: DocEsc | null; cor: string; onClose: () => void; onPronto: () => void }) {
  const [nome, setNome] = useState('');
  useEffect(() => { if (doc) setNome(doc.nome); }, [doc]);
  const salvar = () => { if (!doc || !nome.trim()) return; mudarDoc(doc, { nome: nome.trim() }).then(() => { onClose(); onPronto(); }, falha); };
  return (
    <Sheet visible={!!doc} onClose={onClose} title={t('Renomear')}>
      <View style={{ padding: 16, gap: 12 }}>
        <Field value={nome} onChangeText={setNome} autoFocus maxLength={200} onSubmitEditing={salvar} />
        <Btn label={t('Salvar')} cor={cor} disabled={!nome.trim()} onPress={salvar} />
      </View>
    </Sheet>
  );
}

// ─── Reuniões ────────────────────────────────────────────────────────────────────────────────
// Texto 27 (B6): quem participa (não sempre a equipe toda), Vou / Não vou, repetir toda semana, link externo (Meet, Zoom,
// Teams), pauta, duração real (texto 24), ata com o editor + "Gerar ata com a minha IA" e tarefas da reunião.
function Reunioes({ g, cor, onEntrar }: { g: MeuGrupo; cor: string; onEntrar: () => void }) {
  const [lista, setLista] = useState<Reuniao[] | null>(null);
  const [resp, setResp] = useState<RespReuniao[]>([]);
  const [membros, setMembros] = useState<Membro[]>([]);
  const [erro, setErro] = useState('');
  const [nova, setNova] = useState(false);
  const [antigas, setAntigas] = useState(false);
  const [ata, setAta] = useState<Reuniao | null>(null);
  const [pautas, setPautas] = useState<Set<string>>(new Set());
  const [eu, setEu] = useState('');
  const { info } = useInfoMembros(g.id);
  const carregar = useCallback(() => {
    reunioesDe(g.id).then(r => { setLista(r); setErro(''); respostasDe(r.map(x => x.id)).then(setResp, () => setResp([])); }, e => setErro(String(e?.message ?? e)));
  }, [g.id]);
  useEffect(() => { carregar(); meuId().then(setEu); membrosDe(g.id).then(setMembros, () => {}); }, [carregar, g.id]);
  const hoje = today();
  // 0.77: a reunião aparece no horário deste dispositivo (quem marcou pode estar em outro fuso)
  const local = (r: Reuniao): Reuniao => { if (!r.fuso) return r; const x = noAparelho({ time: r.hora, tz: r.fuso }, r.data); return { ...r, data: x.dia, hora: x.hora ?? r.hora }; };
  // a que se repete toda semana aparece sempre na próxima vez
  const vez = (r: Reuniao): Reuniao => (r.repetir === 'semanal' ? { ...r, data: proximaVez(r.data, hoje) } : r);
  const ordem = (lista ?? []).map(local).map(vez).sort((a, b) => (a.data + a.hora).localeCompare(b.data + b.hora));
  const proximas = ordem.filter(r => r.data >= hoje);
  const passadas = ordem.filter(r => r.data < hoje).reverse();
  const podeEditar = g.admin || papelDe(info, eu) !== 've';
  const podeMarcar = (g.admin || g.membros_enviam) && podeEditar;
  const novoTela = useNovoComNota(podeMarcar ? { rotulo: t('Marcar reunião'), fn: () => setNova(true) } : null);
  const nomeDe = (id: string) => membros.find(m => m.user_id === id)?.nome || t('Sem nome');
  const convidados = (r: Reuniao) => (r.participantes?.length ? r.participantes : null);
  const participo = (r: Reuniao) => !convidados(r) || convidados(r)!.includes(eu) || r.criada_por === eu;
  const cancelar = (r: Reuniao) => Alert.alert(t('Cancelar a reunião "{nome}"?', { nome: r.titulo }), r.repetir === 'semanal'
    ? t('Ela se repete toda semana: cancelando, sai de todas as semanas, da lista e dos Recebidos da equipe.') : t('Ela sai da lista e dos Recebidos da equipe.'), [
    { text: t('Voltar'), style: 'cancel' }, { text: t('Cancelar reunião'), style: 'destructive', onPress: () => cancelarReuniao(r.id).then(carregar, falha) }]);
  const responder = (r: Reuniao, x: 'vou' | 'naovou') => responderReuniao(r.id, x).then(carregar, falha);
  const Cartao = ({ r, passada }: { r: Reuniao; passada?: boolean }) => {
    const rs = resp.filter(x => x.reuniao_id === r.id);
    const meu = rs.find(x => x.user_id === eu)?.resposta;
    const vao = rs.filter(x => x.resposta === 'vou').map(x => x.nome || t('Sem nome'));
    const naoVao = rs.filter(x => x.resposta === 'naovou').map(x => x.nome || t('Sem nome'));
    const quem = convidados(r) ? convidados(r)!.map(nomeDe).join(', ') : t('a equipe toda');
    const verPauta = pautas.has(r.id);
    return (
      <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6, opacity: passada ? 0.75 : 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View style={{ alignItems: 'center', width: 54 }}>
            <Text style={{ color: cor, fontWeight: '800', fontSize: 16 }}>{horaTela(r.hora)}</Text>
            <Text style={{ color: C.sub, fontSize: 11 }}>{r.data === hoje ? t('Hoje') : r.data === addDays(hoje, 1) ? t('Amanhã') : brYmd(r.data).slice(0, 5)}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>🎥 {r.titulo}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{r.duracao_real && r.duracao_real !== r.duracao
              ? t('{n} min (marcada para {m} min)', { n: r.duracao_real, m: r.duracao }) : t('{n} min', { n: r.duracao })} · {t('marcada por {nome}', { nome: r.criada_nome })}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={2}>👥 {t('Com {quem}', { quem })}{r.repetir === 'semanal' ? ' · 🔁 ' + t('toda semana') : ''}</Text>
          </View>
        </View>
        {(!!vao.length || !!naoVao.length) && (
          <Text style={{ color: C.sub, fontSize: 12 }}>{[vao.length ? '✅ ' + t('Vão: {n}', { n: vao.join(', ') }) : '', naoVao.length ? '❌ ' + t('Não vão: {n}', { n: naoVao.join(', ') }) : ''].filter(Boolean).join('  ·  ')}</Text>
        )}
        {!passada && participo(r) && (
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Chip small cor="#16A34A" on={meu === 'vou'} label={'✅ ' + t('Vou')} onPress={() => responder(r, 'vou')} />
            <Chip small cor={C.danger} on={meu === 'naovou'} label={'❌ ' + t('Não vou')} onPress={() => responder(r, 'naovou')} />
          </View>
        )}
        {!!r.descricao && (
          <Pressable onPress={() => setPautas(p => { const n = new Set(p); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })}>
            <Text style={{ color: C.sub, fontSize: 13, fontWeight: '700' }}>{verPauta ? '▾' : '▸'} 📝 {t('Pauta')}</Text>
            {verPauta && <Text style={{ color: C.sub, fontSize: 13 }}>{r.descricao}</Text>}
          </Pressable>
        )}
        <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
          {!passada && (g.admin || r.criada_por === eu) && <Pressable onPress={() => cancelar(r)} style={{ paddingHorizontal: 8, paddingVertical: 8 }}><Text style={{ color: C.danger }}>{t('Cancelar')}</Text></Pressable>}
          <Pressable onPress={() => setAta(r)} style={{ paddingHorizontal: 10, paddingVertical: 8 }}>
            <Text style={{ color: cor, fontWeight: '700' }}>📝 {r.ata ? t('Ata') : passada ? t('Escrever a ata') : t('Ata')}</Text></Pressable>
          {!passada && !!r.link && <Pressable onPress={() => Linking.openURL(r.link!).catch(falha)} style={{ borderWidth: 1, borderColor: cor, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 7 }}>
            <Text style={{ color: cor, fontWeight: '700' }}>🔗 {t('Abrir o link')}</Text></Pressable>}
          {!passada && <Pressable onPress={onEntrar} style={{ backgroundColor: cor, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 8 }}>
            <Text style={{ color: '#fff', fontWeight: '700' }}>{t('Entrar na reunião')}</Text></Pressable>}
        </View>
      </View>
    );
  };
  return (
    <View style={{ gap: 10 }}>
      {podeMarcar && <Btn label={'＋ ' + t('Marcar reunião')} cor={cor} onPress={() => setNova(true)} />}
      <Text style={{ color: C.mute, fontSize: 12 }}>{t('Cada pessoa convidada recebe a reunião em Recebidos e coloca no Planner com um toque, com o alerta.')}</Text>
      {!lista && !erro && <ActivityIndicator color={cor} />}
      {!!erro && <Text style={{ color: C.danger }}>{erro}</Text>}
      {lista && !proximas.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 10 }}>{t('Nenhuma reunião marcada.')}</Text>}
      {proximas.map(r => <Cartao key={r.id} r={r} />)}
      {!!passadas.length && (
        <Pressable onPress={() => setAntigas(!antigas)}><Text style={{ color: C.sub, marginTop: 8 }}>{antigas ? '▾' : '▸'} {t('Anteriores ({n})', { n: passadas.length })}</Text></Pressable>
      )}
      {antigas && passadas.map(r => <Cartao key={r.id} r={r} passada />)}
      <NovaReuniao visible={nova} g={g} cor={cor} membros={membros} eu={eu} onClose={() => setNova(false)} onPronto={carregar} />
      {novoTela}
      <AtaSheet r={ata} g={g} cor={cor} membros={membros} podeEditar={podeEditar} onClose={() => setAta(null)} onPronto={carregar} />
    </View>
  );
}

/** A ata da reunião: o editor das caixas, "Gerar ata com a minha IA" (a partir da pauta e das anotações) e as tarefas da reunião. */
function AtaSheet({ r, g, cor, membros, podeEditar, onClose, onPronto }: { r: Reuniao | null; g: MeuGrupo; cor: string; membros: Membro[]; podeEditar: boolean;
  onClose: () => void; onPronto: () => void }) {
  const [texto, setTexto] = useState('');
  const [rico, setRico] = useState<RicoCampo | null>(null);
  const [chave, setChave] = useState(0);
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novaTarefa, setNovaTarefa] = useState(false);
  const tarefas = useTarefasEsc(g.id);
  useEffect(() => { if (r) { setTexto(r.ata ?? ''); setRico(null); setChave(k => k + 1); } }, [r]);
  const daReuniao = (tarefas ?? []).filter(x => r && x.reuniao_id === r.id);
  const gerar = async () => {
    if (!r) return;
    if (!(await iaPronta())) return Alert.alert(t('IA não ligada'), t('Ligue a sua IA em Ajustes › Inteligência artificial.'));
    if (!texto.trim() && !r.descricao.trim()) return Alert.alert(t('Ata'), t('Escreva primeiro as anotações da reunião (o que foi falado e decidido); a IA organiza em ata.'));
    setGerando(true);
    try {
      const sistema = `Você escreve atas de reunião de escritório, curtas e claras, em ${idiomaResposta()}. Use as seções: Participantes, Assuntos tratados, Decisões, Tarefas (com responsável e prazo quando houver). Não invente nada que não esteja nas anotações. Responda só com o texto da ata, sem markdown.`;
      const entrada = [`Reunião: ${r.titulo}`, `Data: ${brYmd(r.data)} ${r.hora}`, `Participantes: ${(r.participantes?.length ? r.participantes.map(id => membros.find(m => m.user_id === id)?.nome ?? '') : membros.map(m => m.nome)).filter(Boolean).join(', ')}`,
        r.descricao ? `Pauta:\n${r.descricao}` : '', texto ? `Anotações:\n${texto}` : ''].filter(Boolean).join('\n\n');
      const out = (await perguntar(sistema, entrada)).trim();
      setTexto(out); setRico(null); setChave(k => k + 1);
    } catch (e) { falha(e); } finally { setGerando(false); }
  };
  const salvar = async () => {
    if (!r) return;
    setSalvando(true);
    try { await salvarAta(r.id, texto); onClose(); onPronto(); } catch (e) { falha(e); } finally { setSalvando(false); }
  };
  return (
    <Sheet visible={!!r} onClose={onClose} title={r ? t('Ata: {nome}', { nome: r.titulo }) : ''}>
      <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        {!!r && <Text style={{ color: C.sub, fontSize: 12 }}>{brYmd(r.data)} · {horaTela(r.hora)}{r.duracao_real ? ' · ' + t('{n} min', { n: r.duracao_real }) : ''}</Text>}
        {podeEditar ? (<>
          <CampoRico key={chave} valor={texto} rico={rico} cor={cor} aba="escritorio" converter soTexto onChange={(tx, rc) => { setTexto(tx.slice(0, 20000)); setRico(rc); }} />
          <Btn label={gerando ? t('Gerando...') : '✨ ' + t('Gerar ata com a minha IA')} cor={cor} outline disabled={gerando} onPress={gerar} />
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('A IA organiza a pauta e as suas anotações em ata. Confira antes de salvar.')}</Text>
        </>) : <Text style={{ color: C.text }}>{texto || t('Ainda sem ata.')}</Text>}
        <Section t={t('Tarefas da reunião ({n})', { n: daReuniao.length })} />
        {daReuniao.map(x => <Text key={x.id} style={{ color: C.text }}>{x.situacao === 'feito' ? '✅' : '⬜'} {x.titulo}{x.responsavel_nome ? ' · 👤 ' + x.responsavel_nome : ''}{x.prazo ? ' · 📅 ' + brYmd(x.prazo).slice(0, 5) : ''}</Text>)}
        {podeEditar && <Btn label={'＋ ' + t('Tarefa da reunião')} cor={cor} outline onPress={() => setNovaTarefa(true)} />}
        {podeEditar && <Btn label={salvando ? '...' : t('Salvar a ata')} cor={cor} disabled={salvando} onPress={salvar} />}
      </ScrollArrastavel>
      <TarefaSheet visible={novaTarefa} g={g} cor={cor} reuniao={r?.id ?? null} membros={membros} onClose={() => setNovaTarefa(false)} onPronto={() => recarregarTarefas(g.id)} />
    </Sheet>
  );
}

function NovaReuniao({ visible, g, cor, membros, eu, onClose, onPronto }: { visible: boolean; g: MeuGrupo; cor: string; membros: Membro[]; eu: string; onClose: () => void; onPronto: () => void }) {
  const [titulo, setTitulo] = useState('');
  const [data, setData] = useState(today());
  const [hora, setHora] = useState('09:00');
  const [duracao, setDuracao] = useState(60);
  const [descricao, setDescricao] = useState('');
  const [pautaRico, setPautaRico] = useState<RicoCampo | null>(null);
  const [picker, setPicker] = useState<'data' | 'hora' | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [quem, setQuem] = useState<Set<string>>(new Set());   // vazio = a equipe toda
  const [repetir, setRepetir] = useState(false);
  const [link, setLink] = useState('');
  useEffect(() => { if (visible) { setTitulo(''); setData(today()); const d = new Date(Date.now() + 3600e3); setHora(`${String(d.getHours()).padStart(2, '0')}:00`); setDuracao(60); setDescricao(''); setPautaRico(null);
    setQuem(new Set()); setRepetir(false); setLink(''); } }, [visible]);
  const linkOk = !link.trim() || /^https?:\/\/\S+$/i.test(link.trim());
  const salvar = async () => {
    if (!titulo.trim() || !linkOk) return;
    setSalvando(true);
    // quem marca participa sempre
    const participantes = quem.size ? [...new Set([...quem, eu])] : [];
    try { await marcarReuniao(g.id, { titulo: titulo.trim(), data, hora, duracao, descricao: descricao.trim() }, { participantes, repetir: repetir ? 'semanal' : '', link: link.trim() }); onClose(); onPronto(); }
    catch (e) { falha(e); } finally { setSalvando(false); }
  };
  const horaDate = () => { const [h, m] = hora.split(':').map(Number); const d = new Date(); d.setHours(h, m, 0, 0); return d; };
  const outros = membros.filter(m => m.user_id !== eu);
  const diaSemana = parseYmd(data).toLocaleDateString(undefined, { weekday: 'long' });
  return (
    <Sheet visible={visible} onClose={onClose} title={t('Marcar reunião')}>
      <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <Field value={titulo} onChangeText={setTitulo} placeholder={t('Assunto (ex.: Reunião semanal)')} maxLength={200} />
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Pressable onPress={() => setPicker('data')} style={{ flex: 1, backgroundColor: C.card2, borderRadius: 12, padding: 14 }}>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Data')}</Text><Text style={{ color: C.text, fontSize: 16 }}>📅 {brYmd(data)}</Text></Pressable>
          <Pressable onPress={() => setPicker('hora')} style={{ flex: 1, backgroundColor: C.card2, borderRadius: 12, padding: 14 }}>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Hora')}</Text><Text style={{ color: C.text, fontSize: 16 }}>🕘 {horaTela(hora)}</Text></Pressable>
        </View>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Duração')}</Text>
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          {[30, 60, 90, 120].map(n => <Chip key={n} on={duracao === n} label={n < 60 ? `${n} min` : `${n / 60}`.replace('.5', ',5') + ' h'} cor={cor} onPress={() => setDuracao(n)} />)}
        </View>
        <Chip on={repetir} cor={cor} label={'🔁 ' + t('Repetir toda semana ({dia})', { dia: diaSemana })} onPress={() => setRepetir(!repetir)} />
        {/* B6: escolher quem participa (não sempre a equipe toda) */}
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Quem participa')}</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          <Chip small on={!quem.size} cor={cor} label={'👥 ' + t('A equipe toda')} onPress={() => setQuem(new Set())} />
          {outros.map(m => <Chip key={m.user_id} small on={quem.has(m.user_id)} cor={cor} label={m.nome || t('Sem nome')}
            onPress={() => setQuem(q => { const n = new Set(q); if (n.has(m.user_id)) n.delete(m.user_id); else n.add(m.user_id); return n; })} />)}
        </View>
        <Field value={link} onChangeText={setLink} placeholder={'🔗 ' + t('Link externo (opcional: Meet, Zoom, Teams)')} autoCapitalize="none" keyboardType="url" maxLength={500} />
        {!linkOk && <Text style={{ color: C.warn, fontSize: 12, fontWeight: '700' }}>{t('O link precisa começar com https://')}</Text>}
        {/* texto 21 (A4): a pauta usa o editor das caixas (Texto, Checklist, Lista); vai para a equipe como texto */}
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Pauta (opcional)')}</Text>
        <CampoRico valor={descricao} rico={pautaRico} cor={cor} aba="escritorio" converter soTexto
          onChange={(tx, rico) => { setDescricao(tx.slice(0, 2000)); setPautaRico(rico); }} />
        {/* texto 24 (4b): quando falta algo, o motivo aparece logo acima do botão */}
        {!titulo.trim() && <Text style={{ color: C.warn, fontSize: 12, fontWeight: '700' }}>✍️ {t('Escreva o assunto da reunião para poder marcar.')}</Text>}
        <Btn label={salvando ? '...' : quem.size ? t('Marcar e avisar quem participa') : t('Marcar e avisar a equipe')} cor={cor} disabled={salvando || !titulo.trim() || !linkOk} onPress={salvar} />
      </ScrollArrastavel>
      {picker === 'data' && <PickerModal mode="date" cor={cor} value={parseYmd(data)} onClose={() => setPicker(null)}
        onPick={d => { setPicker(null); setData(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`); }} />}
      {picker === 'hora' && <PickerModal mode="time" cor={cor} value={horaDate()} onClose={() => setPicker(null)} onPick={d => { setPicker(null); setHora(hm(d)); }} />}
    </Sheet>
  );
}

// ─── Equipe ──────────────────────────────────────────────────────────────────────────────────
function Equipe({ g, cor, onSaiu, soMinhas, setSoMinhas }: { g: MeuGrupo; cor: string; onSaiu: () => void; soMinhas: boolean; setSoMinhas: (v: boolean) => void }) {
  const [membros, setMembros] = useState<Membro[] | null>(null);
  const [eu, setEu] = useState('');
  const [add, setAdd] = useState(false);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [codigo, setCodigo] = useState(g.codigo);
  const [novaTarefa, setNovaTarefa] = useState(false);
  const [novoAviso, setNovoAviso] = useState(false);
  const [editarMembro, setEditarMembro] = useState<Membro | null>(null);
  const { info, carregar: carregarInfo } = useInfoMembros(g.id);
  const carregar = useCallback(() => { membrosDe(g.id).then(setMembros, falha); }, [g.id]);
  // texto 27 (B5): quem administra edita sempre; os outros conforme o papel (Edita · Só vê)
  const podeEditar = g.admin || papelDe(info, eu) !== 've';
  const soVe = () => Alert.alert(t('Só vê'), SO_VE());
  // B9: o + da Equipe: tarefa, aviso no mural ou (quem administra) amigos do app
  const novoTela = useNovoComNota(null, [
    ...(podeEditar ? [{ label: '✅ ' + t('Nova tarefa'), onPress: () => setNovaTarefa(true) }, { label: '📌 ' + t('Novo aviso no mural'), onPress: () => setNovoAviso(true) }] : []),
    ...(g.admin ? [{ label: '➕ ' + t('Adicionar amigos do app'), onPress: () => setAdd(true) }] : []),
  ], podeEditar ? t('Nova tarefa') : undefined);
  useEffect(() => { carregar(); meuId().then(setEu); }, [carregar]);
  const opcoes = (m: Membro) => {
    if (!g.admin || m.user_id === eu) return;
    const a: Action[] = [{ label: '🏷️ ' + t('Cargo e papel'), onPress: () => setEditarMembro(m) }, m.admin
      ? { label: '👤 ' + t('Tirar de administrador'), onPress: () => setAdmin(g.id, m.user_id, false).then(carregar, falha) }
      : { label: '⭐ ' + t('Tornar administrador'), onPress: () => setAdmin(g.id, m.user_id, true).then(carregar, falha) }];
    if (g.dono === eu) a.push({ label: '👑 ' + t('Passar o Admin'), onPress: () => Alert.alert(t('Passar o Admin para {nome}?', { nome: m.nome || t('Sem nome') }),
      t('{nome} passa a ser quem cuida do escritório (pode apagá-lo). Você continua na equipe como administrador (quem recebe pode mudar isso depois).', { nome: m.nome || t('Sem nome') }), [
        { text: t('Cancelar'), style: 'cancel' },
        { text: t('Passar'), onPress: () => passarAdmin(g.id, m.user_id).then(() => { carregar(); Alert.alert(t('Pronto'), t('{nome} agora cuida do escritório.', { nome: m.nome || t('Sem nome') })); }, falha) }]) });
    if (m.user_id !== g.dono) a.push({ label: '🚪 ' + t('Tirar do escritório'), destructive: true, onPress: () => removerMembro(g.id, m.user_id).then(() => { carregar(); recarregarGrupos(); }, falha) });
    setMenu({ t: m.nome, a });
  };
  // texto 24 (4a): apagar o escritório é item grande: confirmação dizendo o que sai + a senha do app (ou biometria)
  const ident = useConfirmarIdentidade(cor);
  const sair = () => {
    const dono = g.dono === eu;
    Alert.alert(dono ? t('Apagar o escritório?') : t('Sair do escritório?'),
      dono ? t('Serão apagados para todos: os documentos, as reuniões marcadas e as gravações do escritório "{nome}". A conversa continua no Mensagens.', { nome: g.nome }) : t('Você deixa de ver a conversa, os documentos e as reuniões.'), [
        { text: t('Cancelar'), style: 'cancel' },
        { text: dono ? t('Apagar') : t('Sair'), style: 'destructive', onPress: async () => {
          if (dono && !(await ident.pedir(t('Confirme para apagar o escritório')))) return;
          (dono ? apagarGrupo(g.id) : sairGrupo(g.id)).then(() => { recarregarGrupos(); onSaiu(); }, falha);
        } }]);
  };
  const total = membros?.length ?? g.membros;
  return (
    <View style={{ gap: 10 }}>
      {!podeEditar && <Text style={{ color: C.sub, fontSize: 12 }}>👁 {t('Seu papel aqui é "Só vê": você acompanha avisos, tarefas, reuniões e documentos.')}</Text>}
      {!!eu && <Mural g={g} cor={cor} eu={eu} total={total} podeEditar={podeEditar} novo={novoAviso} setNovo={setNovoAviso} />}
      {!!eu && <TarefasEquipe g={g} cor={cor} eu={eu} podeEditar={podeEditar} soMinhas={soMinhas} setSoMinhas={setSoMinhas} nova={novaTarefa} setNova={setNovaTarefa} />}
      {g.admin && <Btn label={'➕ ' + t('Adicionar amigos do app')} cor={cor} onPress={() => setAdd(true)} />}
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <Btn label={'🟢 ' + t('Convidar pelo WhatsApp')} cor="#16A34A" onPress={() => convidarPara({ nome: g.nome, codigo }, true)} style={{ flex: 1 }} />
        <Btn label={'🔗 ' + t('Outros')} cor={cor} outline onPress={() => convidarPara({ nome: g.nome, codigo })} />
      </View>
      <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Código do convite')}</Text>
          <Text style={{ color: C.text, fontSize: 20, fontWeight: '800', letterSpacing: 2 }}>{codigo}</Text>
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('O convite leva o link para baixar o app. Quem entra pelo link cai direto no escritório.')}</Text>
        </View>
        {g.admin && <Pressable onPress={() => Alert.alert(t('Trocar o código?'), t('O link antigo deixa de funcionar.'), [{ text: t('Cancelar'), style: 'cancel' },
          { text: t('Trocar'), onPress: () => trocarCodigo(g.id).then(c => { setCodigo(c); recarregarGrupos(); }, falha) }])}><Text style={{ color: cor }}>{t('Trocar')}</Text></Pressable>}
      </View>
      <Section t={t('Equipe ({n})', { n: membros?.length ?? g.membros })} />
      {!membros && <ActivityIndicator color={cor} />}
      {(membros ?? []).map(m => (
        <Pressable key={m.user_id} onPress={() => opcoes(m)} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: alpha(cor, 0.25), alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: C.text, fontWeight: '800' }}>{(m.nome || '?').trim().charAt(0).toUpperCase()}</Text></View>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.text }} numberOfLines={1}>{m.nome || t('Sem nome')}{m.user_id === eu ? ' (' + t('você') + ')' : ''}</Text>
            {!!cargoDe(info, m.user_id) && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{cargoDe(info, m.user_id)}</Text>}
          </View>
          {m.admin ? <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>⭐ {t('Admin')}</Text>
            : <Text style={{ color: C.sub, fontSize: 12 }}>{papelDe(info, m.user_id) === 've' ? '👁 ' + t('Só vê') : '✏️ ' + t('Edita')}</Text>}
          {g.admin && m.user_id !== eu && <Text style={{ color: C.sub, fontSize: 20, fontWeight: '800' }} accessibilityLabel={t('Mais opções')}>⋮</Text>}
        </Pressable>
      ))}
      <Pressable onPress={sair} style={{ padding: 14, alignItems: 'center', marginTop: 10 }}>
        <Text style={{ color: C.danger }}>{g.dono === eu ? t('Apagar o escritório') : t('Sair do escritório')}</Text></Pressable>
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      {novoTela}
      <MembroSheet g={g} m={editarMembro} info={info} cor={cor} onClose={() => setEditarMembro(null)} onPronto={carregarInfo} />
      {ident.tela}
      <AdicionarAmigos visible={add} g={g} cor={cor} jaTem={new Set((membros ?? []).map(m => m.user_id))} onClose={() => setAdd(false)} onPronto={() => { carregar(); recarregarGrupos(); }} />
    </View>
  );
}

function AdicionarAmigos({ visible, g, cor, jaTem, onClose, onPronto }: { visible: boolean; g: MeuGrupo; cor: string; jaTem: Set<string>; onClose: () => void; onPronto: () => void }) {
  const chat = useChat();
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (visible) { setMarcados(new Set()); carregarAmigos().catch(() => {}); } }, [visible]);
  const amigos = (chat.amigos ?? []).filter(a => a.status === 'aceita' && !jaTem.has(a.id));
  const salvar = async () => {
    setSalvando(true);
    let ok = 0; const erros: string[] = [];
    for (const id of marcados) { try { await adicionarNaEquipe(g.id, id); ok++; } catch (e: any) { erros.push(String(e?.message ?? e)); } }
    setSalvando(false); onClose(); onPronto();
    if (erros.length) Alert.alert(t('{n} adicionada(s)', { n: ok }), erros[0]);
  };
  return (
    <Sheet visible={visible} onClose={onClose} title={t('Adicionar à equipe')}
      right={marcados.size ? <Pressable onPress={salvar} disabled={salvando}><Text style={{ color: cor, fontWeight: '700' }}>{salvando ? '...' : t('Adicionar')}</Text></Pressable> : undefined}>
      <View style={{ padding: 16, gap: 8 }}>
        {!chat.amigos && <ActivityIndicator color={cor} />}
        {chat.amigos && !amigos.length && (
          <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Nenhum amigo para adicionar. Convide pelo WhatsApp ou adicione amigos no Mensagens (código do amigo).')}</Text>
        )}
        {amigos.map(a => {
          const on = marcados.has(a.id);
          return (
            <Pressable key={a.id} onPress={() => setMarcados(s => { const n = new Set(s); if (on) n.delete(a.id); else n.add(a.id); return n; })}
              style={{ backgroundColor: on ? alpha(cor, 0.18) : C.card, borderRadius: 12, padding: 14, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ fontSize: 18 }}>{on ? '☑️' : '⬜'}</Text>
              <Text style={{ color: C.text, flex: 1 }}>{a.nome}</Text>
            </Pressable>
          );
        })}
        {Platform.OS === 'web' && <View style={{ height: 20 }} />}
      </View>
    </Sheet>
  );
}
